import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { norm } from '../utils';
import { 
    FaChartLine, FaSpinner, FaExclamationTriangle, FaBoxOpen, FaCalendarAlt, 
    FaBullseye, FaShoppingCart, FaSyncAlt, FaTruck, FaSave, FaInfoCircle, 
    FaDollarSign, FaClock, FaExclamationCircle, FaCheckCircle, FaEdit,
    FaSearch, FaTimes, FaFilter, FaSort, FaSortUp, FaSortDown,
    FaTable, FaTh, FaArrowUp, FaArrowDown, FaMinus 
} from 'react-icons/fa';
import { debounce } from 'lodash';

// ✅ COMPONENTE SIMPLIFICADO PARA EDIÇÃO INLINE DO LEAD TIME
const LeadTimeEditor = ({ previsao, isEditing, onEdit, onSave, onCancel }) => {
    const [leadTimeLocal, setLeadTimeLocal] = useState(previsao.lead_time_dias || 0);
    const [isLoading, setIsLoading] = useState(false);

    // FORÇA atualização do valor local quando a previsão muda
    useEffect(() => {
        const novoValor = previsao.lead_time_dias || 0;
        setLeadTimeLocal(novoValor);
    }, [previsao.lead_time_dias, previsao.produto_id]);

    const handleSave = async () => {
        if (isLoading) return;
        
        setIsLoading(true);
        try {
            await onSave(previsao.produto_id, leadTimeLocal);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setLeadTimeLocal(previsao.lead_time_dias || 0);
        onCancel();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            handleCancel();
        }
    };

    if (isEditing) {
        return (
            <div className="flex items-center justify-center gap-1">
                <input
                    type="number"
                    value={leadTimeLocal}
                    onChange={(e) => setLeadTimeLocal(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="w-16 px-2 py-1 border rounded text-center text-sm"
                    min="0"
                    max="365"
                    autoFocus
                    disabled={isLoading}
                />
                <button 
                    onClick={handleSave} 
                    className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                    title="Salvar"
                    disabled={isLoading}
                >
                    {isLoading ? <FaSpinner className="animate-spin" size={12} /> : <FaSave size={12} />}
                </button>
                <button 
                    onClick={handleCancel} 
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Cancelar"
                    disabled={isLoading}
                >
                    <FaTimes size={12} />
                </button>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center gap-1">
            <span className="font-medium text-sm">
                {previsao.lead_time_dias || 0} dias
            </span>
            <button 
                onClick={onEdit} 
                className="text-blue-600 hover:text-blue-800 p-1"
                title="Editar lead time"
            >
                <FaEdit size={12} />
            </button>
        </div>
    );
};

const PurchasePrediction = () => {
    const [produtos, setProdutos] = useState([]);
    const [previsoes, setPrevisoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingPrevisoes, setLoadingPrevisoes] = useState(false);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('table');
    const [isMobile, setIsMobile] = useState(false);
    const [editingLeadTime, setEditingLeadTime] = useState({});
    const [filtro, setFiltro] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'urgencia', direction: 'desc' });
    const [statusFilter, setStatusFilter] = useState('todos'); // 'todos', 'urgente', 'medio', 'baixo'
    const [showFilters, setShowFilters] = useState(false);

    // Detectar se é mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
            if (window.innerWidth <= 768) {
                setViewMode('cards');
            } else {
                setViewMode('table');
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Ref para evitar setState após unmount nos fetches assíncronos
    const mountedRef = React.useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        carregarProdutos();
    }, []);

    useEffect(() => {
        if (produtos.length > 0) {
            carregarPrevisoes();
        }
    }, [produtos]);

    const carregarProdutos = async () => {
    try {
        setLoading(true);
        const response = await api.get('/produtos?detalhes=true');
        if (!mountedRef.current) return;
        setProdutos(response.data);
        setError(null);
    } catch (err) {
        if (!mountedRef.current) return;
        console.error('❌ Erro ao carregar produtos:', err);
        setError('Falha ao carregar produtos');
    } finally {
        if (mountedRef.current) setLoading(false);
    }
    };

    const carregarPrevisoes = async () => {
    try {
        setLoadingPrevisoes(true);

        const produtosParaAnalise = produtos.slice(0, 50);

        const previsoesPromises = produtosParaAnalise.map(async (produto) => {
            try {
                const response = await api.get(`/produtos/${produto.id}/previsao`);
                if (!response.data.mensagem) {
                    return {
                        produto_id: produto.id,
                        produto_nome: produto.nome,
                        produto_codigo: produto.codigo_barras,
                        lead_time_dias: produto.lead_time_dias || 0,
                        ...response.data
                    };
                }
                return null;
            } catch (error) {
                console.error(`Erro ao carregar previsão para produto ${produto.id}:`, error);
                return null;
            }
        });

        const resultados = await Promise.all(previsoesPromises);
        if (!mountedRef.current) return;
        setPrevisoes(resultados.filter(p => p !== null));
    } catch (error) {
        if (!mountedRef.current) return;
        console.error('❌ Erro ao carregar previsões:', error);
        setError('Erro ao carregar previsões');
    } finally {
        if (mountedRef.current) setLoadingPrevisoes(false);
    }
    };

    // ✅ FUNÇÃO FINAL ROBUSTA PARA SALVAR LEAD TIME
    const handleSaveLeadTime = async (produtoId, novoLeadTime) => {
        try {
            const leadTimeValue = parseInt(novoLeadTime, 10);
            if (isNaN(leadTimeValue) || leadTimeValue < 0) {
                throw new Error('Lead time deve ser um número válido');
            }

            // 1. Para a edição ANTES de fazer a requisição
            setEditingLeadTime(prev => {
                const newState = { ...prev };
                delete newState[produtoId];
                return newState;
            });
            
            // 2. Faz a requisição para o backend com MERGE explícito
            const response = await api.put(`/produtos/${produtoId}`, {
                lead_time_dias: leadTimeValue
            });
            
            if (response.status !== 200) {
                throw new Error(`Erro no servidor: ${response.status}`);
            }
            
            // 3. Atualiza IMEDIATAMENTE as previsões (única fonte da verdade na tela)
            setPrevisoes(prevPrevisoes => {
                const updated = prevPrevisoes.map(p => 
                    p.produto_id === produtoId 
                        ? { ...p, lead_time_dias: leadTimeValue }
                        : p
                );
                return updated;
            });
            
            // 4. Força um re-render para garantir que a UI seja atualizada
            setTimeout(() => {
                setPrevisoes(prev => [...prev]);
            }, 100);
            
        } catch (err) {
            console.error('❌ Erro ao salvar lead time:', err);
            
            // Em caso de erro, permite edição novamente
            setEditingLeadTime(prev => ({ 
                ...prev, 
                [produtoId]: false 
            }));
            
            alert(`Erro ao salvar lead time: ${err.message}`);
        }
    };

    // Função para determinar cor da urgência
    const getUrgencyColor = (urgencia) => {
        switch (urgencia) {
            case 'alta': return 'text-red-600 bg-red-50 border-red-200';
            case 'média': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
            case 'baixa': return 'text-green-600 bg-green-50 border-green-200';
            default: return 'text-gray-600 bg-gray-50 border-gray-200';
        }
    };

    const getUrgencyIcon = (urgencia) => {
        switch (urgencia) {
            case 'alta': return <FaExclamationTriangle className="text-red-500" />;
            case 'média': return <FaClock className="text-yellow-500" />;
            case 'baixa': return <FaCheckCircle className="text-green-500" />;
            default: return <FaInfoCircle className="text-gray-500" />;
        }
    };

    // Filtrar e ordenar previsões
    const previsoesProcessadas = previsoes
        .filter(previsao => {
            // Filtro por nome
            if (filtro && !norm(previsao.produto_nome).includes(norm(filtro))) {
                return false;
            }
            
            // Filtro por status
            if (statusFilter !== 'todos') {
                if (statusFilter === 'urgente' && previsao.urgencia !== 'alta') return false;
                if (statusFilter === 'medio' && previsao.urgencia !== 'média') return false;
                if (statusFilter === 'baixo' && previsao.urgencia !== 'baixa') return false;
            }
            
            return true;
        })
        .sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.key) {
                case 'produto_nome':
                    aValue = a.produto_nome;
                    bValue = b.produto_nome;
                    break;
                case 'urgencia':
                    const urgenciaOrder = { 'alta': 3, 'média': 2, 'baixa': 1 };
                    aValue = urgenciaOrder[a.urgencia] || 0;
                    bValue = urgenciaOrder[b.urgencia] || 0;
                    break;
                case 'estoque_atual':
                    aValue = a.estoque_atual || 0;
                    bValue = b.estoque_atual || 0;
                    break;
                case 'quantidade_sugerida_compra':
                    aValue = a.quantidade_sugerida_compra || 0;
                    bValue = b.quantidade_sugerida_compra || 0;
                    break;
                case 'valor_compra_sugerida':
                    aValue = a.valor_compra_sugerida || 0;
                    bValue = b.valor_compra_sugerida || 0;
                    break;
                case 'dias_cobertura_atual':
                    aValue = a.dias_cobertura_atual || 0;
                    bValue = b.dias_cobertura_atual || 0;
                    break;
                default:
                    return 0;
            }
            
            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'asc' ? 
                <FaSortUp className="inline ml-1" /> : 
                <FaSortDown className="inline ml-1" />;
        }
        return <FaSort className="inline ml-1" />;
    };

    // ✅ FUNÇÃO PARA OBTER ANÁLISE DE PREÇOS (igual ao ShoppingDashboard)
    const getPriceAnalysis = (produto) => {
        if (produto.analise_precos) {
            return produto.analise_precos;
        }
        
        if (produto.preco_custo_atual) {
            return {
                preco_atual: produto.preco_custo_atual,
                preco_ideal: produto.preco_custo_atual * 0.95,
                preco_maximo_aceitavel: produto.preco_custo_atual * 1.1,
                estrategia: 'estimativa',
                recomendacao: 'Preço estimado baseado no último custo'
            };
        }
        
        return null;
    };

    // ✅ FUNÇÃO PARA ÍCONE DE PREÇO
    const getPriceIcon = (analisePrecos) => {
        if (!analisePrecos) return null;
        
        const diferenca = ((analisePrecos.preco_atual - analisePrecos.preco_ideal) / analisePrecos.preco_ideal) * 100;
        
        if (diferenca <= -5) return <FaArrowDown className="text-green-500" title="Preço abaixo do ideal" />;
        if (diferenca >= 10) return <FaArrowUp className="text-red-500" title="Preço acima do recomendado" />;
        return <FaMinus className="text-yellow-500" title="Preço dentro da faixa" />;
    };

    // Componente Card para Mobile
    const PrevisaoCard = ({ previsao }) => {
        const isEditing = editingLeadTime[previsao.produto_id];
        const analisePrecos = getPriceAnalysis(previsao);

        // --- LÓGICA DO TOOLTIP (MODIFICADA) ---
        let tooltipText = 'Sem dados da última compra';
        if (previsao.last_purchase_quantity) {
            const qtde = previsao.last_purchase_quantity;
            const dias = previsao.last_purchase_duration_days;
            
            if (previsao.last_purchase_duration_type === 'current') {
                // Estoque > 0
                tooltipText = `Última compra: ${qtde} unid. (Este lote está durando há ${dias !== null ? dias : 'N/D'} dias)`;
            } else if (previsao.last_purchase_duration_type === 'past') {
                // Estoque == 0
                tooltipText = `Última compra: ${qtde} unid. (Este lote durou ${dias !== null ? dias : 'N/D'} dias)`;
            } else {
                // Fallback
                tooltipText = `Última compra: ${qtde} unid. (Duração N/D)`;
            }
        }
        // --- FIM DA LÓGICA DO TOOLTIP ---

        return (
            <div className={`rounded-lg border p-4 mb-4 ${getUrgencyColor(previsao.urgencia)}`}>
                <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            {getUrgencyIcon(previsao.urgencia)}
                            <h3 className="font-semibold text-gray-800">{previsao.produto_nome}</h3>
                            {getPriceIcon(analisePrecos)}
                        </div>
                        <div className="text-sm text-gray-500">
                            Código: {previsao.produto_codigo}
                        </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${getUrgencyColor(previsao.urgencia)}`}>
                        {previsao.urgencia?.toUpperCase()}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                    <div className="bg-white rounded-lg p-3 border">
                        <div className="text-gray-500 text-xs mb-1">Estoque Atual</div>
                        <div className="font-bold text-gray-800">{previsao.estoque_atual || 0}</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border">
                        <div className="text-gray-500 text-xs mb-1">Cobertura</div>
                        <div className="font-bold text-gray-800">{previsao.dias_cobertura_atual || 0} dias</div>
                    </div>

                    {/* --- CARD MODIFICADO --- */}
                    <div className="bg-white rounded-lg p-3 border">
                        <div className="text-gray-500 text-xs mb-1">Comprar</div>
                        <div 
                            className="font-bold text-blue-600 flex items-center gap-1.5 cursor-help"
                            title={tooltipText}
                        >
                            {previsao.quantidade_sugerida_compra || 0} unid
                            <FaInfoCircle className="text-gray-400" />
                        </div>
                    </div>
                    {/* --- FIM DA MODIFICAÇÃO --- */}

                    <div className="bg-white rounded-lg p-3 border">
                        <div className="text-gray-500 text-xs mb-1">Preço Target</div>
                        <div className="font-bold text-green-600">
                            {analisePrecos ? `R$ ${analisePrecos.preco_ideal.toFixed(2)}` : 'N/A'}
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600">Lead Time:</span>
                    <LeadTimeEditor
                        previsao={previsao}
                        isEditing={isEditing}
                        onEdit={() => setEditingLeadTime(prev => ({ ...prev, [previsao.produto_id]: true }))}
                        onSave={handleSaveLeadTime}
                        onCancel={() => setEditingLeadTime(prev => {
                            const newState = { ...prev };
                            delete newState[previsao.produto_id];
                            return newState;
                        })}
                    />
                </div>

                <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="text-sm">
                        <strong>Quando comprar:</strong> {previsao.data_ideal_pedido}
                    </div>
                    {analisePrecos && (
                        <div className="mt-2 text-xs text-gray-600">
                            💰 {analisePrecos.recomendacao || `Preço ideal: R$ ${analisePrecos.preco_ideal.toFixed(2)}`}
                        </div>
                    )}
                    {previsao.recomendacoes && previsao.recomendacoes.length > 0 && (
                        <div className="mt-2 text-xs text-gray-600">
                            💡 {previsao.recomendacoes[0]}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="container mx-auto p-4 md:p-6 lg:p-8">
                <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200">
                    <div className="text-center py-8">
                        <FaSpinner className="animate-spin text-orange-500 text-4xl mx-auto mb-4" />
                        <p className="text-gray-600 font-medium">Carregando produtos...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto p-4 md:p-6 lg:p-8">
                <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200">
                    <div className="text-center py-8">
                        <FaExclamationTriangle className="mx-auto text-red-500 mb-4" size={32} />
                        <p className="text-red-600 font-medium mb-4">{error}</p>
                        <button 
                            onClick={carregarProdutos}
                            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                        >
                            Tentar Novamente
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Estatísticas
    const stats = {
        total: previsoesProcessadas.length,
        urgentes: previsoesProcessadas.filter(p => p.urgencia === 'alta').length,
        medios: previsoesProcessadas.filter(p => p.urgencia === 'média').length,
        baixos: previsoesProcessadas.filter(p => p.urgencia === 'baixa').length,
        valorTotal: previsoesProcessadas.reduce((total, p) => {
            const analisePrecos = getPriceAnalysis(p);
            return total + (analisePrecos ? analisePrecos.preco_ideal * p.quantidade_sugerida_compra : 0);
        }, 0)
    };

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200">
                {/* Header */}
                <div className="flex items-center mb-6">
                    <span className="text-3xl mr-3" aria-hidden="true">🔮</span>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">Previsão de Compras</h1>
                        <p className="text-gray-600 text-sm mt-1">
                            {stats.total} produtos • {stats.urgentes} urgentes • R$ {stats.valorTotal.toFixed(2)} total
                        </p>
                    </div>
                </div>

                {/* Controles */}
                <div className="mb-6 space-y-4">
                    {/* Barra de busca */}
                    <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Buscar produtos..."
                            value={filtro}
                            onChange={(e) => setFiltro(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500"
                        />
                        {filtro && (
                            <button
                                onClick={() => setFiltro('')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <FaTimes />
                            </button>
                        )}
                    </div>

                    {/* Controles de filtro e visualização */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`px-4 py-2 rounded-lg transition-colors ${
                                    showFilters ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                            >
                                <FaFilter className="mr-2" />
                                Filtros
                            </button>
                            
                            <button
                                onClick={carregarPrevisoes}
                                disabled={loadingPrevisoes}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                            >
                                {loadingPrevisoes ? (
                                    <FaSpinner className="animate-spin mr-2" />
                                ) : (
                                    <FaSyncAlt className="mr-2" />
                                )}
                                Atualizar
                            </button>
                        </div>

                        {!isMobile && (
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setViewMode('table')}
                                    className={`px-4 py-2 rounded-lg transition-colors ${
                                        viewMode === 'table' ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-700'
                                    }`}
                                >
                                    <FaTable className="mr-2" />
                                    Tabela
                                </button>
                                <button
                                    onClick={() => setViewMode('cards')}
                                    className={`px-4 py-2 rounded-lg transition-colors ${
                                        viewMode === 'cards' ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-700'
                                    }`}
                                >
                                    <FaTh className="mr-2" />
                                    Cards
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Filtros expandidos */}
                    {showFilters && (
                        <div className="p-4 bg-gray-50 rounded-lg border">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Filtrar por Urgência
                                    </label>
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                                    >
                                        <option value="todos">Todos ({stats.total})</option>
                                        <option value="urgente">🚨 Urgentes ({stats.urgentes})</option>
                                        <option value="medio">⚠️ Médios ({stats.medios})</option>
                                        <option value="baixo">✅ Baixos ({stats.baixos})</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Estatísticas */}
                <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                            <FaExclamationTriangle className="text-red-500" />
                            <span className="font-medium text-red-700">Urgentes</span>
                        </div>
                        <div className="text-2xl font-bold text-red-600">{stats.urgentes}</div>
                    </div>
                    
                    <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                            <FaClock className="text-yellow-500" />
                            <span className="font-medium text-yellow-700">Médios</span>
                        </div>
                        <div className="text-2xl font-bold text-yellow-600">{stats.medios}</div>
                    </div>
                    
                    <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                            <FaCheckCircle className="text-green-500" />
                            <span className="font-medium text-green-700">Baixos</span>
                        </div>
                        <div className="text-2xl font-bold text-green-600">{stats.baixos}</div>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                            <FaDollarSign className="text-blue-500" />
                            <span className="font-medium text-blue-700">Valor Target</span>
                        </div>
                        <div className="text-xl font-bold text-blue-600">R$ {stats.valorTotal.toFixed(0)}</div>
                    </div>
                </div>

                {/* Loading das previsões */}
                {loadingPrevisoes && (
                    <div className="text-center py-8">
                        <FaSpinner className="animate-spin text-orange-500 text-2xl mx-auto mb-2" />
                        <p className="text-gray-600">Calculando previsões...</p>
                    </div>
                )}

                {/* Conteúdo */}
                {!loadingPrevisoes && previsoesProcessadas.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <FaChartLine className="mx-auto text-4xl text-gray-300 mb-4" />
                        <p className="text-gray-500 text-lg mb-2">Nenhuma previsão encontrada</p>
                        <p className="text-gray-400 text-sm">
                            {filtro 
                                ? `Nenhum produto encontrado para "${filtro}"`
                                : 'Ajuste os filtros para ver mais resultados'
                            }
                        </p>
                    </div>
                ) : !loadingPrevisoes && viewMode === 'table' ? (
                    <div className="overflow-x-auto -mx-4 md:mx-0">
                        <div className="inline-block min-w-full align-middle px-4 md:px-0">
                            <table className="min-w-full border-collapse border border-gray-300">
                                <thead>
                                    <tr style={{ backgroundColor: "#5B4740", color: "#FFF7F5" }}>
                                        <th 
                                            className="p-2 text-left cursor-pointer hover:bg-opacity-80"
                                            onClick={() => handleSort('produto_nome')}
                                        >
                                            Produto {getSortIcon('produto_nome')}
                                        </th>
                                        <th 
                                            className="p-2 text-center cursor-pointer hover:bg-opacity-80 hide-on-mobile"
                                            onClick={() => handleSort('urgencia')}
                                        >
                                            Urgência {getSortIcon('urgencia')}
                                        </th>
                                        <th 
                                            className="p-2 text-center cursor-pointer hover:bg-opacity-80"
                                            onClick={() => handleSort('estoque_atual')}
                                        >
                                            Estoque {getSortIcon('estoque_atual')}
                                        </th>
                                        <th 
                                            className="p-2 text-center cursor-pointer hover:bg-opacity-80"
                                            onClick={() => handleSort('quantidade_sugerida_compra')}
                                        >
                                            Comprar {getSortIcon('quantidade_sugerida_compra')}
                                        </th>
                                        <th className="p-2 text-center hide-on-mobile">
                                            Preço Target
                                        </th>
                                        <th 
                                            className="p-2 text-center cursor-pointer hover:bg-opacity-80 hide-on-mobile"
                                            onClick={() => handleSort('dias_cobertura_atual')}
                                        >
                                            Cobertura {getSortIcon('dias_cobertura_atual')}
                                        </th>
                                        <th className="p-2 text-center">Lead Time</th>
                                        <th className="p-2 text-center hide-on-mobile">Quando Comprar</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previsoesProcessadas.map((previsao) => {
                                        const analisePrecos = getPriceAnalysis(previsao);
                                        
                                        // --- LÓGICA DO TOOLTIP (MODIFICADA) ---
                                        let tooltipText = 'Sem dados da última compra';
                                        if (previsao.last_purchase_quantity) {
                                            const qtde = previsao.last_purchase_quantity;
                                            const dias = previsao.last_purchase_duration_days;
                                            
                                            if (previsao.last_purchase_duration_type === 'current') {
                                                // Estoque > 0
                                                tooltipText = `Última compra: ${qtde} unid. (Este lote está durando há ${dias !== null ? dias : 'N/D'} dias)`;
                                            } else if (previsao.last_purchase_duration_type === 'past') {
                                                // Estoque == 0
                                                tooltipText = `Última compra: ${qtde} unid. (Este lote durou ${dias !== null ? dias : 'N/D'} dias)`;
                                            } else {
                                                // Fallback
                                                tooltipText = `Última compra: ${qtde} unid. (Duração N/D)`;
                                            }
                                        }
                                        // --- FIM DA LÓGICA DO TOOLTIP ---

                                        return (
                                            <tr key={previsao.produto_id} className="border border-gray-300 hover:bg-gray-50">
                                                <td className="p-2">
                                                    <div>
                                                        <div className="flex items-center gap-1 font-medium">
                                                            {previsao.produto_nome}
                                                            {getPriceIcon(analisePrecos)}
                                                        </div>
                                                        <div className="text-xs text-gray-500 md:hidden">
                                                            {getUrgencyIcon(previsao.urgencia)} {previsao.urgencia}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-2 text-center hide-on-mobile">
                                                    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${getUrgencyColor(previsao.urgencia)}`}>
                                                        {getUrgencyIcon(previsao.urgencia)}
                                                        {previsao.urgencia}
                                                    </div>
                                                </td>
                                                <td className="p-2 text-center font-medium">
                                                    {previsao.estoque_atual || 0}
                                                </td>

                                                {/* --- TABELA MODIFICADA --- */}
                                                <td className="p-2 text-center font-bold text-blue-600">
                                                    <span 
                                                        title={tooltipText}
                                                        className="flex items-center justify-center gap-1.5 cursor-help"
                                                    >
                                                        {previsao.quantidade_sugerida_compra || 0}
                                                        <FaInfoCircle className="text-gray-400 hover:text-blue-500" />
                                                    </span>
                                                </td>
                                                {/* --- FIM DA MODIFICAÇÃO --- */}

                                                <td className="p-2 text-center font-bold text-green-600 hide-on-mobile">
                                                    {analisePrecos ? `R$ ${analisePrecos.preco_ideal.toFixed(2)}` : 'N/A'}
                                                </td>
                                                <td className="p-2 text-center hide-on-mobile">
                                                    {previsao.dias_cobertura_atual || 0} dias
                                                </td>
                                                <td className="p-2 text-center">
                                                    <LeadTimeEditor
                                                        previsao={previsao}
                                                        isEditing={editingLeadTime[previsao.produto_id]}
                                                        onEdit={() => setEditingLeadTime(prev => ({ ...prev, [previsao.produto_id]: true }))}
                                                        onSave={handleSaveLeadTime}
                                                        onCancel={() => setEditingLeadTime(prev => {
                                                            const newState = { ...prev };
                                                            delete newState[previsao.produto_id];
                                                            return newState;
                                                        })}
                                                    />
                                                </td>
                                                <td className="p-2 text-center hide-on-mobile">
                                                    <span className={`font-medium ${
                                                        previsao.data_ideal_pedido === 'AGORA' ? 'text-red-600' :
                                                        previsao.data_ideal_pedido === 'Esta semana' ? 'text-yellow-600' :
                                                        'text-green-600'
                                                    }`}>
                                                        {previsao.data_ideal_pedido}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : !loadingPrevisoes && (
                    <div className="space-y-4">
                        {previsoesProcessadas.map((previsao) => (
                            <PrevisaoCard key={previsao.produto_id} previsao={previsao} />
                        ))}
                    </div>
                )}

            </div>
        </div>
    );
};

export default PurchasePrediction;